/**
 * Layer-0 knowledge in the world (WORKBOARD I5, §12; rulings 25–26): drones in flight and the
 * viewer's team's sightings, read from the match state each frame and drawn — nothing here writes
 * state. The hosts call `sync` after their simulation step.
 *
 *   - **Drones** (state.drones, core/drones.ts): the I5 model on its route, nose along the route,
 *     banking into turns, prop spinning, its LED blinking slowly (blue a scout, red a strike drone —
 *     67.27 (a): it replaced the flat disc on the ground the size of its cone of sight, which the owner
 *     read as a projected cone). A drone brought down tumbles out of the sky; one whose route or
 *     endurance ends is simply gone (it is spent).
 *   - **Sightings** (state.sightings, core/towers.ts + drones.ts) for the viewer's team: a drone's
 *     identified sighting is the team-coloured diamond, a tower's radar dot the neutral contact
 *     circle (rendering/trackMarker.ts). K2: both come from the team picture (core/picture.ts: the
 *     team's shared eyes and its instruments, never the truth). Both fade with age and are dropped when faded — the
 *     perishable information made visible. Markers read through terrain (that is the point of a
 *     radar dot) but not through the planet. One that is off the screen — beside you or behind — shows
 *     as an arrow at the screen's edge pointing to it (owner, §12.1: radar dots need a display at layer 0).
 *
 * The fade times are presentation until the awareness core gives sightings a per-second
 * confidence (WORKBOARD §13, L1 / K1); then `confidenceOf` reads that instead.
 */
import * as THREE from 'three';
import type { DroneState, GameState } from '../types';
import { TEAMS, PHYS_DT } from '../constants';
import { teamPicture } from '../core/picture';
export { SIGHTING_FADE_SECONDS, confidenceOf } from '../core/picture';
import { dronePos, droneGroundDir } from '../core/drones';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { personPointOn } from '../core/fineTerrain';

/** X2's mark in the ground view (amber, as the play's and the order marks' accents; it was drawn black). */
const MARK_COLOR = '#ffd27a';
const vecNorm = (v: { x: number; y: number; z: number }) => { const l = Math.hypot(v.x, v.y, v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; };
import { unitPaletteFor } from './unitPalette';
import { buildDroneModel, bakeDroneModel, updateDroneModel, type DroneModel } from './droneModel';
import { drawTrackMarker, markerStep, trackMarkerStyle, RADAR_DOT_COLOR, type TrackMarkerKind } from './trackMarker';
import { MarkerPoints } from './markerPoints';
import { disposeObjectTree } from './meshBake';
import { buildGrenade, SMOKE_LOOK } from './fieldKit';
import { THROW_LOOK } from './personModel';

/** 69.47: the charge's colours (the specialists sheet's C4): the olive film, the detonator, the timer box and its light, the firing wire and its lead. */
const CHARGE_LOOK = { film: '#6a6545', det: '#8b8f93', timer: '#1c1d20', lit: '#ff4a3a', dark: '#3a0f0c', wire: '#2f6fc8', lead: '#7fd0ff' };

/** On-screen marker size (a sprite with sizeAttenuation off: a fraction of the view height, roughly). */
const MARKER_SCALE = 0.05;
const MARKER_STEPS = 5;
const FALL_SECONDS = 1.6;
const BLIP_SCALE = 0.04;
/** A sighting's blip: its texture set as it is shown. */
const blipMaterial = () => new THREE.SpriteMaterial({ transparent: true, depthTest: false, depthWrite: false, sizeAttenuation: false, fog: false });
const BLIP_MARGIN = 0.9;   // how far toward the edge a blip sits, in normalised screen units

/**
 * Where an off-screen point's edge blip goes: given its position in view space (camera at the origin,
 * looking down −Z), the vertical field of view and the aspect, the point on the screen's edge (normalised
 * device coordinates, ±margin) in its direction and the arrow's angle in screen pixels. Null when the
 * point is on the screen. Behind the camera the direction is the side to turn toward.
 */
export function edgeBlip(view: { x: number; y: number; z: number }, fovYDeg: number, aspect: number, margin = BLIP_MARGIN): { x: number; y: number; angle: number } | null {
  const t = Math.tan((fovYDeg * Math.PI) / 360);
  const ahead = view.z < 0;
  if (ahead) {
    const nx = view.x / (-view.z * t * aspect), ny = view.y / (-view.z * t);
    if (Math.abs(nx) <= 1 && Math.abs(ny) <= 1) return null;
  }
  // the direction on the screen: the projection ahead, the turn toward it behind (no divide by a negative depth)
  let dx = view.x / (t * aspect), dy = view.y / t;
  if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) dy = -1;          // dead behind: point down, "turn round"
  const k = margin / Math.max(Math.abs(dx), Math.abs(dy));
  const x = dx * k, y = dy * k;
  return { x, y, angle: Math.atan2(y, x * aspect) };
}

interface DroneView {
  holder: THREE.Group;
  model: DroneModel;
  forward: THREE.Vector3 | null;
  up: THREE.Vector3;          // the local up where it last flew (the fall drops along it)
  bank: number;
  fall: { t: number; vel: THREE.Vector3; spin: THREE.Vector3; up: THREE.Vector3 } | null;
}

const _up = new THREE.Vector3(), _fwd = new THREE.Vector3(), _x = new THREE.Vector3(), _p = new THREE.Vector3(), _q = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _v = new THREE.Vector3();

export class Layer0Views {
  private drones = new Map<number, DroneView>();
  /** 67.73's orbit census: the in-view markers as one point draw (rendering/markerPoints.ts; made with the first marker — it needs a canvas). */
  private markers: MarkerPoints | null = null;
  private blips: THREE.Sprite[] = [];
  private textures = new Map<string, THREE.Texture>();
  private readonly markerGroup = new THREE.Group();

  /**
   * 68.14 follow-up (the owner: "there's a big green diamond over every enemy, then also a small red square"): at the
   * commander's stops the overlay draws the enemy (rendering/commandOverlay.ts), so the shoulder's markers stand down.
   */
  trackMarkersHidden = false;
  /** `viewerTeam`: whose knowledge the markers show (null = none). */
  constructor(private readonly scene: THREE.Scene, private readonly camera: THREE.Camera, public viewerTeam: number | null) {
    this.markerGroup.renderOrder = 20;
    scene.add(this.markerGroup);
  }

  /** 384: a throwaway of the markers' points and a blip (mapped, as shown: the map is in its program's key), both made with the first sighting — for the shader warm-up (rendering/programWarm.ts). */
  warmParts(): THREE.Object3D[] {
    const blip = blipMaterial();
    blip.map = new THREE.Texture();
    return [new MarkerPoints(MARKER_SCALE, 20).points, new THREE.Sprite(blip)];
  }

  sync(state: GameState, dt: number, tick: number): void {
    this.syncDrones(state, dt);
    this.syncGrenades(state, dt);
    this.syncCharges(state, dt, tick);
    this.syncSightings(state, tick);
  }

  // ---------------------------------------------------------------------------
  // 69.47 step 1: the engineers' placed charges (design_sheets/specialists.html's charge: A the timer, B the wire)
  // ---------------------------------------------------------------------------

  /** Each placed charge drawn, and its light's material (a timer's blinks, faster in its last 3 s; the wire's lead stays lit). */
  private chargeViews = new Map<number, { group: THREE.Group; light: THREE.MeshBasicMaterial; mats: THREE.Material[] }>();
  private chargeClock = 0;

  private syncCharges(state: GameState, dt: number, tick: number): void {
    this.chargeClock += dt;
    const live = new Set<number>();
    const size = PERSON_BODY_HEIGHT * personSetOf(state).k * 1.5;   // (a touch large, as the grenades, so a placing reads)
    for (const c of state.charges ?? []) {
      live.add(c.id);
      let v = this.chargeViews.get(c.id);
      if (!v) {
        const group = new THREE.Group(), mats: THREE.Material[] = [];
        const box = (w: number, h: number, d: number, color: string, x: number, y: number, z: number, basic = false) => {
          const mat = basic ? new THREE.MeshBasicMaterial({ color }) : new THREE.MeshLambertMaterial({ color });
          mats.push(mat);
          const m = new THREE.Mesh(new THREE.BoxGeometry(w * size, h * size, d * size), mat);
          m.position.set(x * size, y * size, z * size);
          group.add(m);
          return mat;
        };
        box(0.16, 0.035, 0.07, CHARGE_LOOK.film, 0, 0.018, 0);   // the block in its film
        box(0.03, 0.012, 0.03, CHARGE_LOOK.det, 0.05, 0.04, 0);   // the detonator in it
        const light = c.fuse === 'timer'
          ? (box(0.06, 0.02, 0.045, CHARGE_LOOK.timer, -0.04, 0.045, 0), box(0.03, 0.006, 0.014, CHARGE_LOOK.lit, -0.04, 0.058, 0.008, true) as THREE.MeshBasicMaterial)
          : (box(0.14, 0.006, 0.006, CHARGE_LOOK.wire, 0.13, 0.01, 0.02), box(0.012, 0.012, 0.012, CHARGE_LOOK.lead, 0.2, 0.012, 0.02, true) as THREE.MeshBasicMaterial);
        group.position.set(c.point.x, c.point.y, c.point.z);
        const up = vecNorm(c.surfaceDir);
        group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(up.x, up.y, up.z));
        group.rotateY((c.id * 2.39996) % (Math.PI * 2));   // (each its own way round: the id, no randomness at draw time)
        this.scene.add(group);
        v = { group, light, mats };
        this.chargeViews.set(c.id, v);
      }
      if (c.fuse === 'timer' && c.blowTick !== undefined) {
        const left = (c.blowTick - tick) * PHYS_DT, rate = left < 3 ? 5 : 1;
        v.light.color.set(Math.floor(this.chargeClock * rate * 2) % 2 ? CHARGE_LOOK.dark : CHARGE_LOOK.lit);
      }
    }
    for (const [id, v] of this.chargeViews) if (!live.has(id)) { this.scene.remove(v.group); disposeObjectTree(v.group); for (const m of v.mats) m.dispose(); this.chargeViews.delete(id); }
  }

  // ---------------------------------------------------------------------------
  // 65.15: grenades in flight (lane E's models, 65.12) and the smoke clouds
  // ---------------------------------------------------------------------------

  /** Each grenade drawn: its model, how long it has flown (s), whether a hand threw it (67.60: drawn held, then caught up), and the sim's path so far (t, x, y, z). */
  private grenadeViews = new Map<number, { group: THREE.Group; age: number; hand: boolean; path: number[] }>();
  private cloudViews = new Map<number, { group: THREE.Group; mat: THREE.MeshLambertMaterial }>();

  private syncGrenades(state: GameState, dt: number): void {
    const k = personSetOf(state).k;
    const live = new Set<number>();
    for (const g of state.grenades ?? []) {
      live.add(g.id);
      let v = this.grenadeViews.get(g.id);
      if (!v) {
        const group = buildGrenade(g.kind, unitPaletteFor(TEAMS[g.teamIndex % TEAMS.length])).group;
        group.scale.setScalar(k * 1.5);   // a touch large, so a throw reads at a distance
        this.scene.add(group);
        const owner = state.players.find(p => p.id === g.ownerId);
        v = { group, age: 0, hand: THROW_LOOK.on && !!owner && !owner.mount, path: [] };   // (a vehicle's smoke leaves its dispensers at once)
        this.grenadeViews.set(g.id, v);
      } else v.age += dt;
      grenadeDrawnAt(v, g.pos);
      if (!g.resting) v.group.rotation.x += 0.25;   // tumbling
    }
    for (const [id, v] of this.grenadeViews) if (!live.has(id)) { this.scene.remove(v.group); disposeObjectTree(v.group); this.grenadeViews.delete(id); }
    const standing = new Set<number>();
    for (const c of state.smokes ?? []) {
      standing.add(c.id);
      let v = this.cloudViews.get(c.id);
      if (!v) {
        const mat = new THREE.MeshLambertMaterial({ color: SMOKE_LOOK.cloud, transparent: true, opacity: 0.92, depthWrite: false });
        const group = new THREE.Group();
        // a few overlapping puffs, offset by the cloud's id (no randomness at draw time)
        const puffs: Array<[number, number, number, number]> = [[0, 0.1, 0, 1], [0.45, -0.1, 0.2, 0.7], [-0.4, 0, -0.25, 0.75], [0.1, 0.35, -0.35, 0.6]];
        puffs.forEach(([x, y, z, r], i) => {
          const m = new THREE.Mesh(new THREE.IcosahedronGeometry(c.radius * r, 2), mat);
          const turn = (c.id * 1.7 + i) % (Math.PI * 2);
          m.position.set((x * Math.cos(turn) - z * Math.sin(turn)) * c.radius, y * c.radius, (x * Math.sin(turn) + z * Math.cos(turn)) * c.radius);
          group.add(m);
        });
        group.position.set(c.pos.x, c.pos.y, c.pos.z);
        const planet = state.planets[c.planetIndex];
        if (planet) group.lookAt(planet.position.x, planet.position.y, planet.position.z);
        this.scene.add(group);
        v = { group, mat };
        this.cloudViews.set(c.id, v);
      }
      v.mat.opacity = 0.92 * Math.min(1, c.left / 2);   // it thins in its last two seconds
    }
    for (const [id, v] of this.cloudViews) if (!standing.has(id)) { this.scene.remove(v.group); disposeObjectTree(v.group); v.mat.dispose(); this.cloudViews.delete(id); }
  }

  // ---------------------------------------------------------------------------
  // Drones
  // ---------------------------------------------------------------------------

  private syncDrones(state: GameState, dt: number): void {
    const ps = personSetOf(state);
    const personHeight = PERSON_BODY_HEIGHT * ps.k;
    const seen = new Set<number>();
    for (const d of state.drones ?? []) {
      seen.add(d.id);
      let v = this.drones.get(d.id);
      if (d.alive) {
        if (!v) { v = this.makeDrone(d, personHeight); this.drones.set(d.id, v); }
        this.flyDrone(v, d, state, dt, ps.droneSpeed);
      } else if (v && !v.fall) {
        if (d.end === 'shotDown') this.startFall(v, ps.droneSpeed);
        else this.removeDrone(d.id);
      }
    }
    for (const [id, v] of this.drones) {
      if (v.fall) this.stepFall(id, v, dt, personHeight);
      else if (!seen.has(id)) this.removeDrone(id);
    }
  }

  private makeDrone(d: DroneState, personHeight: number): DroneView {
    const palette = unitPaletteFor(TEAMS[d.teamIndex % TEAMS.length]);
    // 65.16 / 67.27 (a): a strike drone carries its charge and blinks red, a scout blue; the blink offset by its id
    const model = buildDroneModel(palette, personHeight, { strike: d.variant === 'strike', seed: d.id });
    bakeDroneModel(model);
    const holder = new THREE.Group();
    holder.add(model.root);
    this.scene.add(holder);
    return { holder, model, forward: null, up: new THREE.Vector3(0, 1, 0), bank: 0, fall: null };
  }

  private flyDrone(v: DroneView, d: DroneState, state: GameState, dt: number, speed: number): void {
    const planet = state.planets[d.planetIndex];
    const pos = dronePos(d, state);
    const dir = droneGroundDir(d, planet.radius);
    const ahead = droneGroundDir({ ...d, flown: d.flown + Math.max(1e-4, speed * 0.1) }, planet.radius);
    _up.set(pos.x - planet.position.x, pos.y - planet.position.y, pos.z - planet.position.z).normalize();
    _fwd.set(ahead.x - dir.x, ahead.y - dir.y, ahead.z - dir.z);
    _fwd.addScaledVector(_up, -_fwd.dot(_up));
    if (_fwd.lengthSq() < 1e-14) { if (v.forward) _fwd.copy(v.forward).addScaledVector(_up, -v.forward.dot(_up)); else _fwd.set(1, 0, 0).addScaledVector(_up, -_up.x); }
    _fwd.normalize();
    // bank into the turn: the heading's rate of change about the local up
    let target = 0;
    if (v.forward && dt > 0) {
      const turn = Math.atan2(_q.crossVectors(v.forward, _fwd).dot(_up), v.forward.dot(_fwd)) / dt;
      target = THREE.MathUtils.clamp(turn * 0.35, -0.6, 0.6);
    }
    v.bank += (target - v.bank) * Math.min(1, dt * 6);
    v.forward = (v.forward ?? new THREE.Vector3()).copy(_fwd);
    v.up.copy(_up);
    _x.crossVectors(_up, _fwd);
    v.holder.position.set(pos.x, pos.y, pos.z);
    v.holder.quaternion.setFromRotationMatrix(_m.makeBasis(_x, _up, _fwd));
    updateDroneModel(v.model, { bank: v.bank, pitch: 0, propSpeed: 30 }, dt);
  }

  private startFall(v: DroneView, speed: number): void {
    const fwd = v.forward ?? new THREE.Vector3(1, 0, 0);
    v.fall = { t: 0, vel: fwd.clone().multiplyScalar(speed * 0.6), spin: new THREE.Vector3(3.1, 0.7, 5.3), up: v.up.clone() };
  }

  private stepFall(id: number, v: DroneView, dt: number, personHeight: number): void {
    const f = v.fall!;
    f.t += dt;
    f.vel.addScaledVector(f.up, -personHeight * 40 * dt);   // presentation gravity: a quick, readable drop
    v.holder.position.addScaledVector(f.vel, dt);
    v.model.airframe.rotation.x += f.spin.x * dt;
    v.model.airframe.rotation.z += f.spin.z * dt;
    v.model.prop.rotation.z += f.spin.y * dt;
    if (f.t > FALL_SECONDS) this.removeDrone(id);
  }

  private removeDrone(id: number): void {
    const v = this.drones.get(id);
    if (!v) return;
    this.scene.remove(v.holder); disposeObjectTree(v.holder);
    this.drones.delete(id);
  }

  // ---------------------------------------------------------------------------
  // Sightings
  // ---------------------------------------------------------------------------

  private arrow(color: string): THREE.Texture {
    const key = `arrow:${color}`;
    let t = this.textures.get(key);
    if (!t) {
      const c = document.createElement('canvas'); c.width = c.height = 64;
      const g = c.getContext('2d')!;
      g.translate(32, 32);
      g.beginPath(); g.moveTo(26, 0); g.lineTo(-14, -20); g.lineTo(-6, 0); g.lineTo(-14, 20); g.closePath();   // a chevron pointing +x
      g.lineWidth = 7; g.strokeStyle = '#0b0b18'; g.stroke();
      g.fillStyle = color; g.fill();
      t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
      this.textures.set(key, t);
    }
    return t;
  }

  private blip(i: number): THREE.Sprite {
    let sp = this.blips[i];
    if (!sp) {
      sp = new THREE.Sprite(blipMaterial());
      sp.scale.set(BLIP_SCALE, BLIP_SCALE, 1);
      sp.renderOrder = 21;
      this.markerGroup.add(sp);
      this.blips[i] = sp;
    }
    return sp;
  }

  private syncSightings(state: GameState, tick: number): void {
    let b = 0;
    this.markers?.begin();
    const cam = this.camera as THREE.PerspectiveCamera;
    cam.updateMatrixWorld();
    const fov = cam.isPerspectiveCamera ? cam.fov : 55, aspect = cam.isPerspectiveCamera ? cam.aspect : 1;
    const team = this.viewerTeam;
    const lift = PERSON_BODY_HEIGHT * personSetOf(state).k * 1.5;
    const camPos = this.camera.getWorldPosition(_q);
    if (team !== null && !this.trackMarkersHidden) {
      // K2: beliefs — the team's shared eyes and its instruments, never the enemy's true positions
      for (const s of teamPicture(state, team, tick)) {
        const c = s.confidence;
        const planet = state.planets[s.planetIndex];
        if (!planet) continue;
        _up.set(s.surfaceDir.x, s.surfaceDir.y, s.surfaceDir.z).normalize();
        // the owner's black squares in the sky: a mark's place is on the planet's nominal sphere (core/marks.ts markPos),
        // which floats above low ground — drawn on the ground under it, as a person there would be
        if (s.source === 'mark') { const gp = personPointOn(planet, vecNorm(s.surfaceDir), personSetOf(state), personSetOf(state).hitboxCenter); _p.set(gp.x, gp.y, gp.z).addScaledVector(_up, lift); }
        else _p.set(s.pos.x, s.pos.y, s.pos.z).addScaledVector(_up, lift);
        if (behindPlanet(camPos, _p, planet.position, planet.radius)) continue;
        const kind: TrackMarkerKind = s.source === 'mark' ? 'mark' : s.unitId === undefined ? 'dot' : 'seen';
        const color = kind === 'seen' ? unitPaletteFor(TEAMS[(s.unitTeam ?? 0) % TEAMS.length]).body : kind === 'mark' ? MARK_COLOR : '#000000';   // a mark in its own colour (it was black)
        // off the screen: an arrow at the edge, pointing to it, in its colour and as faded as it is
        const edge = edgeBlip(_v.copy(_p).applyMatrix4(cam.matrixWorldInverse), fov, aspect);
        if (edge) {
          const sp = this.blip(b++);
          const mat = sp.material as THREE.SpriteMaterial;
          const tex = this.arrow(kind === 'seen' ? color : RADAR_DOT_COLOR);
          if (mat.map !== tex) { mat.map = tex; mat.needsUpdate = true; }
          mat.rotation = edge.angle;
          mat.opacity = trackMarkerStyle(c).opacity;
          sp.position.set(edge.x, edge.y, 0.5).unproject(cam);
          sp.visible = true;
          continue;
        }
        // (in view: a point of the markers' one draw, painted as its sprite was — each look once, into the atlas)
        if (!this.markers) { this.markers = new MarkerPoints(MARKER_SCALE, 20); this.markerGroup.add(this.markers.points); this.markers.begin(); }
        const step = markerStep(c, MARKER_STEPS);
        this.markers.add(_p, `${kind}:${step}:${color}`, (g, size) => drawTrackMarker(g, size, kind, (step + 0.5) / MARKER_STEPS, color));
      }
    }
    this.markers?.end();
    for (let i = b; i < this.blips.length; i++) this.blips[i].visible = false;
  }

  dispose(): void {
    for (const id of [...this.drones.keys()]) this.removeDrone(id);
    for (const v of this.grenadeViews.values()) { this.scene.remove(v.group); disposeObjectTree(v.group); }
    this.grenadeViews.clear();
    for (const v of this.cloudViews.values()) { this.scene.remove(v.group); disposeObjectTree(v.group); v.mat.dispose(); }
    this.cloudViews.clear();
    for (const sp of this.blips) (sp.material as THREE.Material).dispose();
    this.blips = [];
    this.markers?.dispose(); this.markers = null;
    for (const t of this.textures.values()) t.dispose();
    this.textures.clear();
    this.scene.remove(this.markerGroup);
  }
}

/** Is `p` hidden from `cam` by the planet's body (a sphere a little inside its surface)? */
function behindPlanet(cam: THREE.Vector3, p: THREE.Vector3, centre: { x: number; y: number; z: number }, radius: number): boolean {
  const r = radius * 0.97;
  const dx = p.x - cam.x, dy = p.y - cam.y, dz = p.z - cam.z;
  const len = Math.hypot(dx, dy, dz);
  if (len < 1e-9) return false;
  const ux = dx / len, uy = dy / len, uz = dz / len;
  const ox = cam.x - centre.x, oy = cam.y - centre.y, oz = cam.z - centre.z;
  const b = ox * ux + oy * uy + oz * uz;
  const c = ox * ox + oy * oy + oz * oz - r * r;
  const disc = b * b - c;
  if (disc < 0) return false;
  const t = -b - Math.sqrt(disc);
  return t > 0 && t < len;
}

/**
 * 67.60: where a thrown grenade is drawn. The sim lets it go the tick the throw comes in; the thrower's arm releases it
 * THROW_LOOK.hold seconds later (personModel's throw), so till then it is in the hand (hidden here) and then it follows the
 * sim's path from the hand, a little behind, catching up over THROW_LOOK.catchUp (its drawn flight time τ runs from 0 at
 * the release to its age by then, never back). A vehicle's smoke and anything past the catch-up: where the sim has it.
 */
export function grenadeDrawnAt(v: { group: THREE.Object3D; age: number; hand: boolean; path: number[] }, pos: { x: number; y: number; z: number }): void {
  const T = THROW_LOOK, settle = T.hold + T.catchUp;
  if (!v.hand || v.age >= settle) { v.group.visible = true; v.group.position.set(pos.x, pos.y, pos.z); if (v.path.length) v.path.length = 0; return; }
  v.path.push(v.age, pos.x, pos.y, pos.z);
  if (v.age < T.hold) { v.group.visible = false; return; }
  const u = (v.age - T.hold) / T.catchUp, e = u * u * (3 - 2 * u), tau = v.age - T.hold * (1 - e);
  const p = v.path;
  let i = 0; while (i + 4 < p.length && p[i + 4] <= tau) i += 4;
  const j = Math.min(i + 4, p.length - 4), span = p[j] - p[i], f = span > 1e-9 ? Math.max(0, Math.min(1, (tau - p[i]) / span)) : 0;
  v.group.visible = true;
  v.group.position.set(p[i + 1] + (p[j + 1] - p[i + 1]) * f, p[i + 2] + (p[j + 2] - p[i + 2]) * f, p[i + 3] + (p[j + 3] - p[i + 3]) * f);
}
